<template>
	<portal
		to="popover"
		:disabled="disablePortal"
		:slim="slim ? true : undefined"
	>
		<div
			:class="[
				'popover',
				...positionClassList,
				...stateClassList,
				hasCloseBtn && 'has-close-btn'
			]"
			:style="{ maxWidth: maxWidth, 'z-index': zIndex }"
			v-if="visibleActive"
			ref="popoverContainer"
			data-testid="popover"
			v-move="container"
		>
			<header
				class="popover__title-container"
				v-if="showHeader"
				data-testid="header"
			>
				<div class="popover__title">
					<slot name="popoverTitle">
						<span
							class="popover__title-icon"
							v-if="state"
							data-testid="title-icon"
						>
							<i
								:class="[
									'icon',
									state === 'negative' && 'icon-warning',
									state === 'warning' && 'icon-warning-01',
									state === 'positive' && 'icon-circle-check'
								]"
								data-testid="icon"
							></i>
						</span>
						{{ title }}
					</slot>
					<ButtonIcon
						variant="topRight"
						class="popover__close-btn"
						iconClassName="icon-remove"
						@click="onClose"
						v-if="hasCloseBtn"
						data-testid="close-btn"
					/>
				</div>
				<Divider
					v-if="hasDivider"
					class="popover__divider"
					variant="horizontal"
				/>
			</header>
			<div class="popover__body">
				<slot name="popoverBody" />
			</div>
			<footer
				class="popover__footer"
				v-if="$slots.popoverFooter !== undefined"
			>
				<slot name="popoverFooter" />
			</footer>
			<div class="popover__arrow" ref="arrow" />
			<PopperPosition
				:trackedRefName="trackedRef"
				:popoverRefName="currentPopoverRef"
				:arrowRefName="$refs.arrow"
				:placement="placement"
				:popperVisible="
					mounted && visibleActive && currentPopoverRef !== undefined
				"
				:modifiers="popperModifiers"
				:trackerWatch="trackedRefName"
				:strategy="positionStrategy"
				v-if="
					mounted && visibleActive && currentPopoverRef !== undefined
				"
			/>
		</div>
	</portal>
</template>

<script>
import ButtonIcon from '@/components/forms/button/button-icon.vue';
import PopperPosition from '@/components/visual/popper-position/popper-position.vue';
import componentPositionMixin from '@/mixins/component-position-mixin';
import componentStateMixin from '@/mixins/component-state-mixin';
import documentClickedMixin from '@/mixins/component-document-clicked-mixin';
import visibleDelayMixin from '@/mixins/visible-delay-mixin';
import Divider from '@/components/visual/divider/divider.vue';

export default {
	name: 'Popover',
	components: {
		ButtonIcon,
		PopperPosition,
		Divider
	},
	mixins: [
		componentPositionMixin,
		componentStateMixin,
		documentClickedMixin,
		visibleDelayMixin
	],
	data() {
		return {
			mounted: false,
			containerReady: false
		};
	},
	props: {
		disablePortal: {
			type: Boolean,
			default: true
		},
		visible: {
			type: Boolean,
			default: false
		},
		hasCloseBtn: {
			type: Boolean,
			default: true
		},
		maxWidth: {
			type: String,
			default: 'auto'
		},

		title: {
			type: String,
			default: ''
		},
		hasDivider: {
			type: Boolean,
			default: false
		},
		delay: {
			type: Number,
			default: 0
		}, //overrides delay in visibleDelayMixin so we can default to no delay
		/**
		 * Set to true so portal won't render a wrapper div
		 */
		slim: {
			type: Boolean,
			default: false
		},
		zIndex: {
			type: Number,
			default: 1
		}
	},
	watch: {
		async visibleActive(newValue) {
			if (newValue) {
				await this.$nextTick();
				this.containerReady = true;
			} else {
				this.containerReady = false;
			}
		}
	},
	mounted() {
		this.mounted = true;
	},
	created() {
		this.documentClickedHandler = this.onDocumentClicked;
	},
	methods: {
		onClose() {
			this.$emit('close');
		},
		onDocumentClicked(target) {
			let el;

			if (this.trackedRef) {
				if (this.trackedRef.$el) {
					el = this.trackedRef.$el;
				} else {
					el = this.trackedRef;
				}

				if (
					this.visibleActive &&
					!el.contains(target) &&
					this.$refs.popoverContainer &&
					!this.$refs.popoverContainer.contains(target)
				) {
					this.onClose();
				}
			}
		}
	},
	computed: {
		currentPopoverRef() {
			let ref = '';

			if (this.mounted && this.containerReady && this.visibleActive) {
				ref = this.$refs.popoverContainer;
			}

			return ref;
		},
		showHeader() {
			return !!this.$slots.popoverTitle || !!this.title;
		}
	}
};
</script>

<style lang="scss" scoped>
@include scrollbar('borderless');
.popover {
	display: inline-block;
	padding: $padding-md;
	font-family: 'Lato', sans-serif;
	min-width: 120px;
	@include dark-light-medium-shadow($type: 'drop-shadow');
	border: 1px solid var(--popover-border);
	border-radius: $border-radius;
	background: var(--popover-bg);
	color: var(--popover-fg);

	&__title-container {
		display: flex;
		flex-direction: column;
		margin-bottom: $gutter-sm;
	}

	&__title {
		display: flex;
		align-items: flex-start;

		@include heading('sm');
		text-align: left;

		.icon {
			font-size: 15px;
			margin-right: $gutter-md;
			color: $ui-positive;
		}
	}

	&__close-btn {
		margin-left: auto;

		:deep(.icon) {
			color: var(--popover-fg);
			font-size: $txt-supporting-font-size;
			margin-top: $gutter-xs
		}
	}

	&__body {
		font-size: $txt-supporting-font-size;
		text-align: left;
		overflow-y: auto;
	}

	&__footer {
		position: relative;
		margin-top: $gutter-xl;

		&::before {
			@include scroll-fade(var(--bg-0));
		}
	}
}

/*------------------------------------*\
  #POSITION-STYLES
  This section is only for popover position CSS. Default position - Top Middle
  These classes are added/removed as needed via dev logic.
\*------------------------------------*/
.popover {
	$arrowSizeBase: 16px; // Change this value to adjust the size of the arrow
	$arrowSizeA: #{$arrowSizeBase};
	$arrowSizeB: #{$arrowSizeBase * 2};

	&__arrow {
		width: $arrowSizeB;
		height: $arrowSizeA;
		position: absolute;
		top: 100%;
		left: 50%;

		&::before,
		&::after {
			position: absolute;
			display: block;
			content: '';
			border-style: solid;
			border-width: $arrowSizeA $arrowSizeA 0 $arrowSizeA;
			border-color: transparent;
		}

		&::before {
			top: 0;
			border-top-color: var(--popover-border);
		}

		&::after {
			top: -1px;
			border-top-color: var(--popover-bg);
		}
	}

	&[data-popper-placement^='bottom'] {
		.popover__arrow {
			top: -$arrowSizeA;
			left: 50%;

			&::before,
			&::after {
				border-color: transparent;
				border-width: 0 $arrowSizeA $arrowSizeA $arrowSizeA;
			}

			&::before {
				top: 0;
				border-bottom-color: var(--popover-border);
			}

			&::after {
				top: 1px;
				border-bottom-color: var(--popover-bg);
			}
		}
	}

	&[data-popper-placement='left'] {
		.popover__arrow {
			left: 100%;
			width: $arrowSizeA;
			height: $arrowSizeB;

			&::before,
			&::after {
				border-width: $arrowSizeA 0 $arrowSizeA $arrowSizeA;
				border-top-color: transparent;
			}

			&::before {
				top: initial;
				left: 0;
				border-left-color: var(--popover-border);
			}

			&::after {
				top: initial;
				left: -1px;
				border-left-color: var(--popover-bg);
			}
		}
	}

	&[data-popper-placement='right'] {
		.popover__arrow {
			left: -$arrowSizeA;
			width: $arrowSizeA;
			height: $arrowSizeB;

			&::before,
			&::after {
				border-width: $arrowSizeA $arrowSizeA $arrowSizeA 0;
				border-top-color: transparent;
			}

			&::before {
				top: initial;
				left: 0px;
				border-right-color: var(--popover-border);
			}

			&::after {
				top: initial;
				left: 1px;
				border-right-color: var(--popover-bg);
			}
		}
	}
}

/*------------------------------------*\
  #STATE-TYPE-STYLES
  This section is only for popover CSS change via state type.
  Neutral state is the default, CSS for that is located in the base component styles section.
  These classes are added/removed as needed via dev logic
\*------------------------------------*/
.popover {
	&.is-warning {
		color: var(--ui-warning);

		.icon {
			color: var(--ui-warning);
		}
	}

	&.is-negative {
		color: var(--ui-negative);

		.icon {
			color: var(--ui-negative);
		}
	}

	&.is-positive {
		color: var(--ui-positive);

		.icon {
			color: var(--ui-positive);
		}
	}
}

@include transition-fade;
</style>
